import { type FormEvent, useState } from 'react';

import type { Inbox } from '../lib/api';

interface InboxPaneProps {
  readonly inboxes: readonly Inbox[];
  readonly selected: string | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly mailDomain: string;
  readonly onSelect: (address: string) => void;
  readonly onCreate: (name: string) => Promise<Inbox | null>;
  readonly onSignOut: () => void;
}

/**
 * The address list and the form that adds to it — the two things the app is
 * actually for. It gets the leftmost pane and the full height of the window so
 * that switching between addresses is one click from anywhere in the app.
 */
export function InboxPane({
  inboxes,
  selected,
  loading,
  error,
  mailDomain,
  onSelect,
  onCreate,
  onSignOut,
}: InboxPaneProps) {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    const trimmed = name.trim();
    if (!trimmed || creating) return;

    setCreating(true);
    const created = await onCreate(trimmed);
    setCreating(false);

    if (created) {
      // Cleared only on success, so a rejected name stays in the field to be
      // corrected rather than making the user type it again.
      setName('');
      onSelect(created.address);
    }
  };

  return (
    <section className="pane pane--inboxes" aria-label="Addresses">
      <div className="brand">
        <span className="brand__mark" aria-hidden="true">
          R
        </span>
        <span className="brand__name">Riser Mail</span>
        <button className="btn btn--ghost" type="button" onClick={onSignOut} title="Sign out">
          Exit
        </button>
      </div>

      <form className="compose-address" onSubmit={submit}>
        <div className="compose-address__row">
          <input
            className="compose-address__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="new-address"
            aria-label={`New address name, at ${mailDomain}`}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            disabled={creating}
          />
          <span className="compose-address__suffix" aria-hidden="true">
            @{mailDomain}
          </span>
        </div>

        <button
          className="btn btn--primary btn--block"
          type="submit"
          disabled={creating || name.trim().length === 0}
          style={{ marginTop: 8 }}
        >
          {creating ? 'Creating…' : '+ Add email'}
        </button>
      </form>

      {error ? (
        <p className="alert alert--inline" role="alert">
          {error}
        </p>
      ) : null}

      <div className="pane__body">
        {loading && inboxes.length === 0 ? (
          <div className="empty">
            <div className="spinner" role="status" aria-label="Loading addresses" />
          </div>
        ) : null}

        {!loading && inboxes.length === 0 ? (
          <div className="empty">
            <span className="empty__title">No addresses yet</span>
            <span className="empty__hint">
              Pick a name above to create your first address. Mail sent to it will appear here.
            </span>
          </div>
        ) : null}

        {inboxes.map((inbox) => (
          <button
            key={inbox.address}
            className="inbox"
            type="button"
            // aria-current marks the selected item for assistive technology;
            // the stylesheet keys the highlight off the same attribute so the
            // two cannot drift apart.
            aria-current={inbox.address === selected}
            onClick={() => onSelect(inbox.address)}
            title={inbox.address}
          >
            <span className="inbox__name">{inbox.address.split('@')[0]}</span>

            {inbox.unreadCount > 0 ? (
              <span className="inbox__badge">{inbox.unreadCount}</span>
            ) : null}
          </button>
        ))}
      </div>
    </section>
  );
}
